/** Dialog to create a new form when template + enrollment period are fixed (e.g. from forms list). User selects the student. */
import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import * as React from "react";
import { InfoAlert } from "src/components/Feedback/InfoAlert";
import { EligibleStudentSelectInput } from "src/components/Inputs/EligibleStudentSelectInput";
import { EnrollmentPeriodSelectInput } from "src/components/Inputs/EnrollmentPeriodSelectInput";
import type {
  ApplicantSummary,
  EligibleFormTemplateSummary,
} from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { validateWithZod } from "src/services/formValidations";
import {
  CreateFormFormSchema,
  CreateFormFormValues,
  CreateFormSelectedApplicant,
  CreateFormSelectedTemplate,
  useCreateFormSubmit,
} from "./useCreateFormSubmit";

interface CreateFormForTemplateDialogProps {
  enrollmentPeriodId: uuid;
  formTemplateId: uuid;
  formTemplateName: string;
  isOpen: boolean;
  onClose: () => void;
  width?: string;
  height?: string;
}

export function CreateFormForTemplateDialog({
  enrollmentPeriodId,
  formTemplateId,
  formTemplateName,
  isOpen,
  onClose,
  width,
  height,
}: CreateFormForTemplateDialogProps) {
  const organization = useOrganization();
  const organizationId = React.useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );

  const [selectedFormTemplate, setSelectedFormTemplate] =
    React.useState<CreateFormSelectedTemplate>(undefined);
  const [selectedApplicant, setSelectedApplicant] =
    React.useState<CreateFormSelectedApplicant>(undefined);

  React.useEffect(() => {
    if (isOpen && formTemplateId && formTemplateName) {
      const template: EligibleFormTemplateSummary = {
        id: formTemplateId,
        name: formTemplateName,
        description: null,
        active: true,
        locked: false,
        can_start: false,
      };
      setSelectedFormTemplate(template);
    }
  }, [isOpen, formTemplateId, formTemplateName]);

  const handleClose = React.useCallback(
    (resetForm: () => void) => {
      resetForm();
      setSelectedFormTemplate(null);
      setSelectedApplicant(null);
      onClose();
    },
    [onClose]
  );

  const {
    handleSubmitForm,
    getAssignedFormsRD,
    alertText,
    alertStatus,
    showSeeFormLink,
    canStartForm,
    handleOnSeeFormClick,
    isSubmitting,
  } = useCreateFormSubmit({
    selectedEnrollmentPeriod: enrollmentPeriodId
      ? { id: enrollmentPeriodId }
      : undefined,
    selectedApplicant,
    selectedFormTemplate,
    onClose: handleClose,
  });

  const initialValues: CreateFormFormValues = React.useMemo(
    () => ({
      organizationId,
      enrollmentPeriodId,
      formTemplateId,
      applicantId: "",
    }),
    [organizationId, enrollmentPeriodId, formTemplateId]
  );

  const onSelectedApplicant = React.useCallback(
    (applicant: ApplicantSummary | null | undefined) => {
      setSelectedApplicant(applicant);
    },
    []
  );

  return (
    <Formik<CreateFormFormValues>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmitForm}
      validate={validateWithZod(CreateFormFormSchema)}
      validateOnBlur
      validateOnMount
      validateOnChange
    >
      {({ values, isValid, resetForm }) => {
        const handleOnClose = () => handleClose(resetForm);
        return (
          <Modal isCentered isOpen={isOpen} onClose={handleOnClose}>
            <ModalOverlay />
            <ModalContent width={width} height={height}>
              <Form noValidate>
                <ModalHeader>Create new form</ModalHeader>

                <ModalBody as={VStack} padding={6} pb={4}>
                  <Flex direction="column" gap={4} width="100%">
                    <EligibleStudentSelectInput
                      name="applicantId"
                      organizationId={organizationId}
                      onSelectedStudent={onSelectedApplicant}
                      isRequired
                    />

                    <EnrollmentPeriodSelectInput
                      name="enrollmentPeriodId"
                      organizationId={organizationId}
                      isRequired
                      placeholder="Select enrollment period"
                      label="Enrollment period"
                      isDisabled
                      isVisible={false}
                    />

                    {alertText && (
                      <InfoAlert status={alertStatus}>
                        <Text fontSize="md">
                          {alertText}
                          {showSeeFormLink && (
                            <Link
                              variant="link"
                              onClick={() =>
                                handleOnSeeFormClick(
                                  values.formTemplateId,
                                  handleOnClose
                                )
                              }
                              color="blue.900"
                              fontWeight="700"
                              fontSize="md"
                              textDecoration="underline"
                              paddingLeft="2"
                            >
                              See form.
                            </Link>
                          )}
                        </Text>
                      </InfoAlert>
                    )}
                  </Flex>
                </ModalBody>

                <ModalFooter gap={2} pt={2} pb={4}>
                  <Button
                    onClick={() => {
                      amplitude.track("Cancel create form clicked");
                      handleOnClose();
                    }}
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={getAssignedFormsRD.isLoading() || isSubmitting}
                    isDisabled={!canStartForm || !isValid}
                    onClick={() => amplitude.track("Create form clicked")}
                  >
                    Create
                  </Button>
                </ModalFooter>
              </Form>
            </ModalContent>
          </Modal>
        );
      }}
    </Formik>
  );
}
